import { useEffect } from "react";
import { useSelector } from "react-redux";
import { AuthService } from "@/services/authService";
import { msUntilRenewal, needsRenewal } from "@/lib/tokenSession";

/**
 * Keep the access token alive for as long as the user is here.
 *
 * The request interceptor already renews ahead of a call, which covers anyone
 * clicking around. It does not cover the two ways a session used to die
 * quietly:
 *
 *   * A tab left open on one screen makes no requests. The token expired, and
 *     with it the WebSocket — which authenticates once, at connect time — so
 *     live updates stopped arriving with nothing on screen to say so. The
 *     first click after that ate a 401.
 *   * A laptop asleep past the token's lifetime woke to an expired token and,
 *     before this, no reason to do anything about it until something failed.
 *
 * So: a timer that fires shortly before expiry, plus a check whenever the tab
 * comes back to the foreground (browsers throttle or skip timers in background
 * tabs, so the timer alone cannot be trusted across a sleep).
 *
 * A failed renewal is deliberately silent here. `AuthService.refresh` is
 * single-flight and the response interceptor signs the user out when the
 * server actually rejects the cookie; a renewal that fails because the network
 * dropped for ten seconds must not end the session.
 */

// Exponential backoff after a failed renewal: 15s, 30s, 60s, 120s, then hold at
// five minutes. Silence is the right response to a failure here — the session
// is still whatever the server says it is — but silence must not mean retrying
// on the floor delay forever.
const BACKOFF_BASE_MS = 15_000;
const BACKOFF_MAX_MS = 5 * 60 * 1000;

const backoffMs = (failures) => Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (failures - 1));

const useSessionRenewal = () => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let timer = null;
    let cancelled = false;
    let failures = 0;

    const renewIfDue = async () => {
      if (cancelled) return;
      if (needsRenewal()) {
        try {
          await AuthService.refresh();
          failures = 0;
        } catch {
          // Leave it to the interceptor — see the note above.
          failures += 1;
        }
      } else {
        failures = 0;
      }
      schedule();
    };

    // Re-read the expiry each time rather than assuming a fixed interval: the
    // token's lifetime is a server setting, and the client should follow it
    // rather than carry its own copy of the number.
    //
    // While renewals keep failing, back off instead. `msUntilRenewal` floors at
    // five seconds when there is no readable token, so a failure the
    // interceptor does not turn into a sign-out — a 500, the network being
    // down — otherwise had every open tab POSTing /auth/refresh every five
    // seconds for as long as it stayed open. Coming back online or to the
    // foreground still retries at once; those are real signals, not a timer.
    const schedule = () => {
      if (cancelled) return;
      clearTimeout(timer);
      timer = setTimeout(renewIfDue, failures ? backoffMs(failures) : msUntilRenewal());
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") renewIfDue();
    };

    schedule();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", renewIfDue);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", renewIfDue);
    };
  }, [isAuthenticated]);
};

export default useSessionRenewal;
